import React from 'react';
import {
  applyOneTimeDiscounts,
  oneTimeDiscountLineLabel,
  ONE_TIME_CATEGORIES,
} from '../lib/oneTimeDiscounts';

// v3.7.8 — One-Time (Non-Recurring) Discounts editor.
//
// The NRC counterpart to ManualDiscounts. Lets a rep attach one or more
// discount lines to a quote's one-time charges, each TARGETED at a specific
// category (Onboarding / Hardware / Activation / Porting) so, e.g., they can
// knock $200 off porting services without touching hardware. Each line is a
// percentage of that category's charge or a flat $ amount, with a note. Every
// line shows on the proposal and the signed Service Order.
//
// Fully controlled: the parent owns the array (persisted to
// quotes.inputs.oneTimeDiscounts) and passes `bases` (the per-category
// customer charge before one-time discounts, onboarding already net of the
// onboarding incentive) so the card can preview each line's $ impact and the
// net, AND so it only offers categories that actually have a charge on THIS
// quote (e.g. Managed IT has only Onboarding).
//
// Like ManualDiscounts, it carries NO margin logic — the quote page owns the
// GM warning (it knows cost) so this stays a focused editor.

const fmt$ = (n) =>
  n != null
    ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

export default function OneTimeDiscounts({ value, onChange, bases }) {
  const rows = Array.isArray(value) ? value : [];
  const resolved = applyOneTimeDiscounts(bases, rows);

  // Only categories with a real charge on this quote are selectable. If none
  // qualify (no one-time charges at all), don't render the card.
  const available = ONE_TIME_CATEGORIES.filter(
    (c) => (Number(resolved.byCategory[c.key]?.base) || 0) > 0
  );
  if (available.length === 0) return null;

  const defaultCategory = available[0].key;

  const update = (i, patch) =>
    onChange(rows.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const add = () =>
    onChange([...rows, { category: defaultCategory, type: 'fixed', value: '', note: '' }]);
  const remove = (i) =>
    onChange(rows.filter((_, idx) => idx !== i));

  const inputBase = {
    fontSize: 12, padding: '5px 7px', border: '1px solid #d1d5db',
    borderRadius: 4, color: '#0f1e3c', background: '#fff', boxSizing: 'border-box',
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: rows.length ? 8 : 0 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0f1e3c' }}>🏷 One-Time Discounts</div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
            Reduce a specific one-time charge. Each line appears on the proposal &amp; Service Order.
          </div>
        </div>
        <button
          type="button"
          onClick={add}
          style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', background: '#0f1e3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          + Add Discount
        </button>
      </div>

      {rows.map((d, i) => {
        const category = available.some((c) => c.key === d.category) ? d.category : defaultCategory;
        const type = d.type === 'pct' ? 'pct' : 'fixed';
        const catBase = Number(resolved.byCategory[category]?.base) || 0;
        const amount = type === 'pct' ? catBase * ((Number(d.value) || 0) / 100) : (Number(d.value) || 0);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <select
              value={category}
              onChange={(e) => update(i, { category: e.target.value })}
              style={{ ...inputBase, width: 108, flex: '0 0 auto', cursor: 'pointer' }}
              title="Which one-time charge to discount"
            >
              {available.map((c) => (
                <option key={c.key} value={c.key}>{c.short}</option>
              ))}
            </select>
            <select
              value={type}
              onChange={(e) => update(i, { type: e.target.value })}
              style={{ ...inputBase, width: 54, flex: '0 0 auto', cursor: 'pointer' }}
              title="Discount type"
            >
              <option value="fixed">$</option>
              <option value="pct">%</option>
            </select>
            <input
              type="number"
              min="0"
              step={type === 'pct' ? '1' : '25'}
              value={d.value ?? ''}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder={type === 'pct' ? '10' : '200'}
              style={{ ...inputBase, width: 72, flex: '0 0 auto', textAlign: 'right' }}
            />
            <input
              type="text"
              value={d.note ?? ''}
              onChange={(e) => update(i, { note: e.target.value })}
              placeholder="Reason — e.g. Porting services"
              style={{ ...inputBase, flex: '1 1 auto', minWidth: 0 }}
            />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', width: 64, textAlign: 'right', flex: '0 0 auto' }}>
              −{fmt$(amount)}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              title="Remove discount"
              style={{ fontSize: 14, lineHeight: 1, padding: '2px 7px', background: '#fff', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', flex: '0 0 auto' }}
            >
              ×
            </button>
          </div>
        );
      })}

      {resolved.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', marginTop: 4, paddingTop: 6 }}>
          <span style={{ fontSize: 10, color: '#6b7280' }}>
            {resolved.lines.length} discount{resolved.lines.length > 1 ? 's' : ''} ·
            {' '}{resolved.lines.map(oneTimeDiscountLineLabel).join(' + ')}
          </span>
          <span style={{ fontSize: 11, color: '#0f1e3c' }}>
            <strong style={{ color: '#b91c1c' }}>−{fmt$(resolved.total)}</strong>
            {resolved.base > 0 && (
              <span style={{ color: '#6b7280' }}> · Net one-time <strong>{fmt$(resolved.finalNRC)}</strong></span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
